import { usePermissions, useTranslate } from "../../../../customHooks";
import { useEffect, useRef, useState } from "react";
import { dataProvider } from "../../../../dataProvider";
import * as DataTypes from "../../../../api/FwaasDataTypes";
import { toast } from "../../../../components";
import { Banner, Button, Link } from "@panwds/react-ui";
import { useIntl } from "react-intl";
import { RouteUri } from "../../../../routeUri";
import { useHistory } from "react-router";
import { Tooltip } from "@panwds/react-ui";
import { InfoIcon } from "@panwds/icons";
import { find } from "lodash";
import { RadioGroup, Select } from "@panwds/react-form";
import { useWatch } from "react-hook-form";

const SecurityPolicies = ({ region, control, setValue }: any) => {
    const translate = useTranslate();
    const intl = useIntl();
    const history = useHistory();
    const { rawPermissions } = usePermissions();
    const defaultRulestackItem = { label: translate(`resources.firewallsV2.defaultRulestack`), value: 'Default Rulestack' };
    const [ruleStackChoices, setRuleStackChoices] = useState<any[] | undefined>([defaultRulestackItem]);
    const [panoramas, setPanoramas] = useState<any[] | undefined>(undefined);
    const [cloudManagers, setCloudManagers] = useState<any[] | undefined>(undefined);
    const isTenantAdmin = rawPermissions && find(rawPermissions, { Policy: "TenantAdmin" });

    useEffect(() => {
        dataProvider.get("ruleStacks", {
            data: {
                Candidate: false,
                Running: true,
                Scope: "Local"
            },
            region: region
        }).then(async (response: DataTypes.IFwaasApiResponse) => {
            //@ts-ignore
            let ret: DataTypes.IFwaasApiResponse = {};
            if (response?.data) {
                //@ts-ignore
                let rulestacks = response.data as string[];
                if (rulestacks) {
                    let choices: any = rulestacks.map((rulestack: any) => ({ text: rulestack, value: rulestack }));
                    choices = choices.sort((first: any, second: any) => {
                        return first.value.localeCompare(second.value, undefined, { sensitivity: 'base' });
                    });

                    // Add 'Default Rulestack' to the choices
                    choices.unshift(defaultRulestackItem);

                    setRuleStackChoices(choices);
                    return;
                }
            } else {
                toast.error(response?.error, { toastId: "ruleStacks-get" });
            }
            return ret;
        }).catch((e: any) => {
            toast.error(e?.error, { toastId: "ruleStacks-get" });
        });
    }, [region]);

    useEffect(() => {
        let apiCall: { resource: string, url?: string, payload?: any } = {
            resource: "settings",
            payload: { panorama: true }
        }
        if (process.env.REACT_APP_SHOW_CM === "true") {
            apiCall = {
                resource: "integrations",
            }
        }

        dataProvider.describe(apiCall.resource, apiCall.url || "", apiCall?.payload || {})
            .then(async (response: DataTypes.IFwaasApiResponse) => {
                let links = Object.keys(response.data.Links);
                let cmLinks: Array<any> = [];
                let pnLinks: Array<any> = [];
                links.map(link => {
                    const value = response.data.Links[link].LinkName ? `${link} (${response.data.Links[link].LinkName})` : link;
                    if (response.data.Links[link]?.hasOwnProperty('CloudManager') && value !== undefined && response.data.Links[link].Status === 'Active') {
                        cmLinks.push({ text: value, value: link });
                    }
                    if (response.data.Links[link]?.hasOwnProperty('Panorama') && value !== undefined) {
                        pnLinks.push({ text: value, value: link });
                    }
                });
                setPanoramas(pnLinks);
                setCloudManagers(cmLinks);
            })
            .catch((e: any) => {
                toast.error(e?.error?.error, { toastId: "support-describe" });
            })
    }, []);

    const getPolicyManagedList = () => {
        let PolicyManagedList = [
            {
                children: (
                    <div className="tw-flex tw-items-center tw-flex-row">
                        <div>{translate(`resources.firewallsV2.rulestack`)}</div>
                        <div><Tooltip label={translate(`resources.firewallsV2.fwCreate.rulestackTooltip`)}>
                            <InfoIcon className="tw-pl-2 tw-fill-current tw-text-disabled" />
                        </Tooltip>
                        </div>
                    </div>
                ),
                value: "Rulestack"
            },
            {
                children: (
                    <div className="tw-flex tw-items-center tw-flex-row">
                        <div>{translate(`resources.firewallsV2.panorama`)}</div>
                        <div><Tooltip label={translate(`resources.firewallsV2.fwCreate.panoramaTooltip`)}>
                            <InfoIcon className="tw-pl-2 tw-fill-current tw-text-disabled" />
                        </Tooltip>
                        </div>
                    </div>
                ),
                value: "Panorama"
            },
        ];
        if (process.env.REACT_APP_SHOW_CM === "true") {
            PolicyManagedList.splice(1, 0,
                {
                    children: (
                        <div className="tw-flex tw-items-center tw-flex-row">
                            <div>{translate(`resources.firewallsV2.strataCloudManager`)}</div>
                            <div><Tooltip label={translate(`resources.firewallsV2.fwCreate.scmTooltip`)}>
                                <InfoIcon className="tw-pl-2 tw-fill-current tw-text-disabled" />
                            </Tooltip>
                            </div>
                        </div>
                    ),
                    value: "Strata Cloud Manager"
                });
        }

        return PolicyManagedList;
    }

    const policyManaged = useWatch({
        control,
        name: "PolicyManaged"
    });
    
    const prevPolicyManagedRef = useRef();

    useEffect(() => {
        const previous = prevPolicyManagedRef.current;
        const current = policyManaged;

        if (previous !== undefined && previous !== current) {
            handlePolicyManaged(current, previous);
        }

        prevPolicyManagedRef.current = current;
    }, [policyManaged]);

    const handlePolicyManaged = (selected: any, previous: any) => {
        if (previous === "Rulestack") {
            setValue("RuleStackName", undefined);
        }
        if (previous === "Strata Cloud Manager") {
            setValue("CMLinkId", undefined);
        }
        if (previous === "Panorama") {
            setValue("LinkId", undefined);
        }
    };

    return (
        <div className="formLayout">
            <RadioGroup
                name="PolicyManaged"
                label="Managed By"
                items={getPolicyManagedList()}
                requiredAsterisk
                dataMetrics="cloudngfw-firewall-create-policy-managed"
                defaultValue={"RuleStack"}
                horizontal
            />
            {/* if policy managed by rulestack */}
            {policyManaged === "Rulestack" &&
            (<Select
                name="RuleStackName"
                label={translate(`resources.firewallsV2.localRulestack`)}
                items={ruleStackChoices}
                dataMetrics="cloudngfw-firewall-create-rulestack-name"
                simpleFormat
                fitToItemWidth
                defaultValue={ruleStackChoices && ruleStackChoices[0]?.value}
            />)}
            {policyManaged === "Strata Cloud Manager" && (
                <>
                    {(cloudManagers === undefined || cloudManagers?.length === 0) && isTenantAdmin && (
                        <div className="tw-space-y-4">
                            <Banner
                                type="inline"
                                appearance="warning"
                                showIcon
                                dataMetrics={"cloudngfw-noCloudManagerMessage-notification-redirect"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    history.push(RouteUri.Integrations)
                                }}
                                actions={
                                    <>
                                        <Button appearance="primary" size="sm" dataMetrics={"cloudngfw-swupgrade-notification-redirect"} onClick={(e) => {
                                            e.preventDefault();
                                            history.push(RouteUri.Integrations)
                                        }}
                                        >
                                            {translate(`resources.firewallsV2.createPolicyManager`)}
                                        </Button>
                                    </>
                                }
                            >
                                {translate(`resources.firewallsV2.noCloudManagerMessage`)}
                            </Banner>
                        </div>
                    )}
                    {(cloudManagers === undefined || cloudManagers?.length === 0) && !isTenantAdmin && (
                        <div className="tw-space-y-4">
                            <Banner
                                type="inline"
                                appearance="warning"
                                showIcon
                                dataMetrics={"cloudngfw-noSPMessageForNonTA-notification-redirect-noTA"}
                            >
                                {translate(`resources.firewallsV2.noSPMessageForNonTA`)}
                            </Banner>
                        </div>
                    )}
                    <Select
                        name="CMLinkId"
                        label={translate(`resources.firewallsV2.policyManager`)}
                        items={cloudManagers}
                        dataMetrics="cloudngfw-firewall-create-link-id"
                        simpleFormat
                        fitToItemWidth
                        requiredAsterisk
                        description={cloudManagers === undefined && isTenantAdmin ?
                            intl.formatMessage(
                                {
                                    id: "resources.firewallsV2.policyManaged.strataDescription",
                                },
                                {
                                    integrationPage: (
                                        <Link
                                            dataMetrics="cloudngfw-scm-page-link"
                                            external
                                            onClick={(e) => {
                                                e.preventDefault();
                                                history.push(RouteUri.Integrations);
                                            }}
                                        >
                                            {translate(`resources.firewallsV2.policyManaged.integrationPage`)}
                                        </Link>
                                    ),
                                }
                            )
                            : isTenantAdmin ? translate(`resources.firewallsV2.linkedCloudManagerMessage`) : null}
                    />
                </>
            )}
            {policyManaged === "Panorama" && (
                <>
                    {(panoramas === undefined || panoramas?.length === 0) && isTenantAdmin && (
                        <div className="tw-space-y-4">
                            <Banner
                                type="inline"
                                appearance="warning"
                                showIcon
                                dataMetrics={"cloudngfw-noPanoramaMessage-notification-redirect"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    history.push(RouteUri.Integrations)
                                }}
                                actions={
                                    <>
                                        <Button appearance="primary" size="sm" dataMetrics={"cloudngfw-swupgrade-notification-redirect"} onClick={(e) => {
                                            e.preventDefault();
                                            history.push(RouteUri.Integrations)
                                        }}>
                                            {translate(`resources.firewallsV2.createPolicyManager`)}
                                        </Button>
                                    </>
                                }
                            >
                                {translate(`resources.firewallsV2.noPanoramaMessage`)}
                            </Banner>
                        </div>
                    )}
                    {(panoramas === undefined || panoramas?.length === 0) && !isTenantAdmin && (
                        <div className="tw-space-y-4">
                            <Banner
                                type="inline"
                                appearance="warning"
                                showIcon
                                dataMetrics={"cloudngfw-noSPMessageForNonTA-panorama-notification-redirect-noTA"}
                            >
                                {translate(`resources.firewallsV2.noSPMessageForNonTA`)}
                            </Banner>
                        </div>
                    )}
                    <Select
                        name="LinkId"
                        label={translate(`resources.firewallsV2.linkedPanorama`)}
                        items={panoramas}
                        dataMetrics="cloudngfw-firewall-create-link-id"
                        simpleFormat
                        fitToItemWidth
                        requiredAsterisk
                        description={panoramas === undefined && isTenantAdmin ?
                            intl.formatMessage(
                                {
                                    id: "resources.firewallsV2.policyManaged.panoramaDescription",
                                },
                                {
                                    integrationPage: (
                                        <Link
                                            dataMetrics="cloudngfw-scm-page-link"
                                            external
                                            onClick={(e) => {
                                                e.preventDefault();
                                                history.push(RouteUri.Integrations);
                                            }}
                                        >
                                            {translate(`resources.firewallsV2.policyManaged.integrationPage`)}
                                        </Link>
                                    ),
                                }
                            )
                            : isTenantAdmin ? translate(`resources.firewallsV2.linkedPanoramaMessage`) : null}
                    />
                </>
            )}
        </div>
    );
};

export default SecurityPolicies;
